import { ApplicationVariablesDto } from 'prequalificationV2/api/ApplicationApi';
import { BorrowerIncomeType, OtherIncomeTypes } from 'enums/BorrowerIncomeType';
import { BorrowerRole } from 'enums/BorrowerRole';
import { IncomeW2Type } from 'api/UnderwritingApi';
import { maskMonetaryValue, removeNonDigits } from 'prequalificationV2/utils/stringUtils';

import {
  HOMEMAKER_UNEMPLOYED_DEFAULT_VALUE,
  INCOME_SLOT_NUMBERS,
  MAXIMUM_INCOME_SOURCES,
  W2_INCOME_TYPES,
} from './borrowerIncomeInfo.constants';
import { CombinedIncomeType, IncomeFormEntry, IncomeFormValues, W2IncomeSlotType } from './borrowerIncomeInfo.types';

const MAP_LOS_INCOME_TYPE_TO_FE_INCOME_TYPE = {
  'Self-Employed': BorrowerIncomeType.SelfEmployed,
  'VA Disability': BorrowerIncomeType.VaDisability,
  'Military Pension': BorrowerIncomeType.MilitaryPension,
  'Employer Pension': BorrowerIncomeType.EmployerPension,
  'Social Security': BorrowerIncomeType.SocialSecurity,
  Rental: BorrowerIncomeType.RentalIncome,
  Other: BorrowerIncomeType.Other,
};

export const getBorrowerVariablePrefix = (borrowerRole: BorrowerRole) => {
  return borrowerRole === BorrowerRole.Borrower ? 'borrower' : 'coborrower';
};

export const getW2IncomeTypeBySlotNumber = (incomeSlotNumber: number) => {
  if (incomeSlotNumber === 1) {
    return BorrowerIncomeType.W2;
  }

  if (incomeSlotNumber === 2) {
    return BorrowerIncomeType.W2SecondEmployer;
  }

  if (incomeSlotNumber === 3) {
    return BorrowerIncomeType.W2ThirdEmployer;
  }

  return null;
};

export const getIncomeTypeVariableName = (borrowerRole: BorrowerRole, incomeSlotNumber: number) => {
  const borrowerVariablePrefix = getBorrowerVariablePrefix(borrowerRole);

  return incomeSlotNumber === 1
    ? `${borrowerVariablePrefix}_income_type`
    : `${borrowerVariablePrefix}_income_type_${incomeSlotNumber}`;
};

export const getIncomeAmountVariableName = (borrowerRole: BorrowerRole, incomeSlotNumber: number) => {
  const borrowerVariablePrefix = getBorrowerVariablePrefix(borrowerRole);

  return incomeSlotNumber === 1
    ? `${borrowerVariablePrefix}_employer_income_amount`
    : `${borrowerVariablePrefix}_employer_income_amount_${incomeSlotNumber}`;
};

export const getIncomeSourceVariableName = (borrowerRole: BorrowerRole, incomeSlotNumber: number) => {
  const borrowerVariablePrefix = getBorrowerVariablePrefix(borrowerRole);

  return incomeSlotNumber === 1
    ? `${borrowerVariablePrefix}_current_employer`
    : `${borrowerVariablePrefix}_current_employer_${incomeSlotNumber}`;
};

export const getPayrollProviderMappingIdVariableName = (borrowerRole: BorrowerRole, incomeSlotNumber: number) => {
  const borrowerVariablePrefix = getBorrowerVariablePrefix(borrowerRole);

  return incomeSlotNumber === 1
    ? `${borrowerVariablePrefix}_payroll_provider_mapping_id`
    : `${borrowerVariablePrefix}_payroll_provider_mapping_id_${incomeSlotNumber}`;
};

export const getIncomeInformationCompleteVariableName = (borrowerRole: BorrowerRole) => {
  const borrowerVariablePrefix = getBorrowerVariablePrefix(borrowerRole);

  return `${borrowerVariablePrefix}_income_information_complete`;
};

export const getIncomeW2TypeVariableName = (borrowerRole: BorrowerRole, incomeSectionNumber: number) => {
  const borrowerVariablePrefix = getBorrowerVariablePrefix(borrowerRole);

  return incomeSectionNumber === 1
    ? `${borrowerVariablePrefix}_income_w2_type`
    : `${borrowerVariablePrefix}_income_w2_type_${incomeSectionNumber}`;
};

export const getOtherIncomeTypeAmountVariableName = (borrowerRole: BorrowerRole, otherIncomeType: OtherIncomeTypes) => {
  const borrowerVariablePrefix = getBorrowerVariablePrefix(borrowerRole);

  return `${borrowerVariablePrefix}_${otherIncomeType}_stated_annual_income`;
};

const getHomemakerOrUnemployedVariableName = (borrowerRole: BorrowerRole) => {
  const borrowerVariablePrefix = getBorrowerVariablePrefix(borrowerRole);

  return `${borrowerVariablePrefix}_homemaker_or_unemployed`;
};

const roundToInteger = (value: unknown): number | undefined => {
  if (value === null || value === undefined || value === '') {
    return undefined;
  }

  const numericValue = Number(value);

  return Number.isNaN(numericValue) ? undefined : Math.round(numericValue);
};

const isOtherIncomeType = (incomeType: string): incomeType is OtherIncomeTypes => {
  return Object.values(OtherIncomeTypes).includes(incomeType as OtherIncomeTypes);
};

const isSupportedIncomeType = (incomeType: string): incomeType is CombinedIncomeType => {
  return Object.values(BorrowerIncomeType).includes(incomeType as BorrowerIncomeType) || isOtherIncomeType(incomeType);
};

export const isW2IncomeType = (incomeType: CombinedIncomeType): incomeType is W2IncomeSlotType => {
  return W2_INCOME_TYPES.includes(incomeType as BorrowerIncomeType);
};

const normalizeIncomeTypeFromLos = (losIncomeTypeValue: string, incomeSlotNumber: number) => {
  if (losIncomeTypeValue === BorrowerIncomeType.W2) {
    return getW2IncomeTypeBySlotNumber(incomeSlotNumber);
  }

  if (isOtherIncomeType(losIncomeTypeValue)) {
    return losIncomeTypeValue;
  }

  return MAP_LOS_INCOME_TYPE_TO_FE_INCOME_TYPE[
    losIncomeTypeValue as keyof typeof MAP_LOS_INCOME_TYPE_TO_FE_INCOME_TYPE
  ] as CombinedIncomeType | undefined;
};

const getFallbackW2IncomeType = (incomeSlotNumber: number) => {
  return getW2IncomeTypeBySlotNumber(incomeSlotNumber);
};

const getIncomeSlotValues = (
  applicationVariablesByName: Record<string, unknown> | undefined,
  borrowerRole: BorrowerRole,
  incomeSlotNumber: number,
) => {
  return {
    incomeTypeValue: applicationVariablesByName?.[getIncomeTypeVariableName(borrowerRole, incomeSlotNumber)],
    incomeAmount: applicationVariablesByName?.[getIncomeAmountVariableName(borrowerRole, incomeSlotNumber)],
    incomeSourceName: applicationVariablesByName?.[getIncomeSourceVariableName(borrowerRole, incomeSlotNumber)],
    payrollProviderMappingId:
      applicationVariablesByName?.[getPayrollProviderMappingIdVariableName(borrowerRole, incomeSlotNumber)],
    incomeW2Type: applicationVariablesByName?.[getIncomeW2TypeVariableName(borrowerRole, incomeSlotNumber)],
  };
};

export const sortIncomeTypes = (combinedIncomeTypes: CombinedIncomeType[]) => {
  const incomeTypeOrder: CombinedIncomeType[] = [
    BorrowerIncomeType.W2,
    BorrowerIncomeType.W2SecondEmployer,
    BorrowerIncomeType.W2ThirdEmployer,
    BorrowerIncomeType.SelfEmployed,
    BorrowerIncomeType.VaDisability,
    BorrowerIncomeType.MilitaryPension,
    BorrowerIncomeType.EmployerPension,
    BorrowerIncomeType.SocialSecurity,
    BorrowerIncomeType.RentalIncome,
    BorrowerIncomeType.HomemakerUnemployed,
    BorrowerIncomeType.Other,
    OtherIncomeTypes.Trust,
    OtherIncomeTypes.ChildSupport,
    OtherIncomeTypes.Alimony,
    OtherIncomeTypes.FosterCare,
    OtherIncomeTypes.Annuities,
    OtherIncomeTypes.TermDisability,
  ];

  return [...combinedIncomeTypes].sort((leftIncomeType, rightIncomeType) => {
    return incomeTypeOrder.indexOf(leftIncomeType) - incomeTypeOrder.indexOf(rightIncomeType);
  });
};

export const createIncomeFormEntry = (incomeType: CombinedIncomeType): IncomeFormEntry => {
  return {
    incomeType: incomeType,
    annualIncome: incomeType === BorrowerIncomeType.HomemakerUnemployed ? HOMEMAKER_UNEMPLOYED_DEFAULT_VALUE : '',
    employerCompany: null,
    w2EmploymentType: null,
    otherDescription: '',
  };
};

export const sortIncomeFormEntries = (incomeFormEntries: IncomeFormEntry[]): IncomeFormEntry[] => {
  const sortedIncomeTypes = sortIncomeTypes(incomeFormEntries.map((incomeFormEntry) => incomeFormEntry.incomeType));

  return sortedIncomeTypes.map((incomeType) => {
    return incomeFormEntries.find((incomeFormEntry) => incomeFormEntry.incomeType === incomeType)!;
  });
};

export const buildIncomeFormValues = (
  applicationVariables: ApplicationVariablesDto | undefined,
  borrowerRole: BorrowerRole,
): IncomeFormValues => {
  const applicationVariablesByName = applicationVariables as unknown as Record<string, unknown> | undefined;
  const incomeFormEntries: IncomeFormEntry[] = [];

  INCOME_SLOT_NUMBERS.forEach((incomeSlotNumber) => {
    const incomeSlotValues = getIncomeSlotValues(applicationVariablesByName, borrowerRole, incomeSlotNumber);
    const incomeTypeValue = incomeSlotValues.incomeTypeValue as string | null | undefined;
    const incomeAmount = incomeSlotValues.incomeAmount as number | null | undefined;
    const incomeSourceName = incomeSlotValues.incomeSourceName as string | null | undefined;
    const payrollProviderMappingId = incomeSlotValues.payrollProviderMappingId as string | null | undefined;
    const incomeW2Type = incomeSlotValues.incomeW2Type as IncomeW2Type | null | undefined;

    let normalizedIncomeType = null;

    if (incomeTypeValue) {
      normalizedIncomeType = normalizeIncomeTypeFromLos(incomeTypeValue, incomeSlotNumber);
    } else if ((incomeSourceName || payrollProviderMappingId) && Number(incomeAmount)) {
      normalizedIncomeType = getFallbackW2IncomeType(incomeSlotNumber);
    }

    if (!normalizedIncomeType || !isSupportedIncomeType(normalizedIncomeType)) {
      return;
    }

    const resolvedIncomeAmount = isOtherIncomeType(normalizedIncomeType)
      ? applicationVariablesByName?.[getOtherIncomeTypeAmountVariableName(borrowerRole, normalizedIncomeType)]
      : incomeAmount;

    const incomeFormEntry = createIncomeFormEntry(normalizedIncomeType);

    incomeFormEntry.annualIncome =
      normalizedIncomeType === BorrowerIncomeType.HomemakerUnemployed
        ? HOMEMAKER_UNEMPLOYED_DEFAULT_VALUE
        : maskMonetaryValue(roundToInteger(resolvedIncomeAmount));

    if (isW2IncomeType(normalizedIncomeType)) {
      incomeFormEntry.employerCompany = {
        label: incomeSourceName || '',
        value: incomeSourceName || '',
        payrollProviderMappingId: payrollProviderMappingId,
      };
      incomeFormEntry.w2EmploymentType = incomeW2Type || null;
    }

    if (normalizedIncomeType === BorrowerIncomeType.Other) {
      incomeFormEntry.otherDescription = incomeSourceName || '';
    }

    incomeFormEntries.push(incomeFormEntry);
  });

  const homemakerOrUnemployedValue = applicationVariablesByName?.[
    getHomemakerOrUnemployedVariableName(borrowerRole)
  ] as boolean | null | undefined;

  if (
    homemakerOrUnemployedValue &&
    !incomeFormEntries.some((incomeFormEntry) => incomeFormEntry.incomeType === BorrowerIncomeType.HomemakerUnemployed)
  ) {
    incomeFormEntries.push(createIncomeFormEntry(BorrowerIncomeType.HomemakerUnemployed));
  }

  Object.values(OtherIncomeTypes).forEach((otherIncomeType) => {
    if (incomeFormEntries.some((incomeFormEntry) => incomeFormEntry.incomeType === otherIncomeType)) {
      return;
    }

    const otherIncomeTypeAmount = applicationVariablesByName?.[
      getOtherIncomeTypeAmountVariableName(borrowerRole, otherIncomeType)
    ] as number | null | undefined;

    if (typeof otherIncomeTypeAmount === 'number' && otherIncomeTypeAmount > 0) {
      const incomeFormEntry = createIncomeFormEntry(otherIncomeType);

      incomeFormEntry.annualIncome = maskMonetaryValue(roundToInteger(otherIncomeTypeAmount));
      incomeFormEntries.push(incomeFormEntry);
    }
  });

  return {
    incomes: sortIncomeFormEntries(incomeFormEntries),
  };
};

export const serializeIncomeFormValues = (incomeFormValues: IncomeFormValues) => {
  const findIncomeFormEntry = (incomeType: CombinedIncomeType) => {
    return incomeFormValues.incomes.find((incomeFormEntry) => incomeFormEntry.incomeType === incomeType);
  };

  const incomes = incomeFormValues.incomes
    .filter((incomeFormEntry) => incomeFormEntry.incomeType !== BorrowerIncomeType.HomemakerUnemployed)
    .map((incomeFormEntry) => ({
      incomeType: incomeFormEntry.incomeType,
      income: Number(removeNonDigits(incomeFormEntry.annualIncome)),
    }));

  const w2IncomeFormEntry = findIncomeFormEntry(BorrowerIncomeType.W2);
  const secondW2IncomeFormEntry = findIncomeFormEntry(BorrowerIncomeType.W2SecondEmployer);
  const thirdW2IncomeFormEntry = findIncomeFormEntry(BorrowerIncomeType.W2ThirdEmployer);
  const otherIncomeFormEntry = findIncomeFormEntry(BorrowerIncomeType.Other);

  return {
    incomes: incomes,
    isHomemakerUnemployed: Boolean(findIncomeFormEntry(BorrowerIncomeType.HomemakerUnemployed)),
    other: otherIncomeFormEntry ? otherIncomeFormEntry.otherDescription : null,
    w2Incomes: {
      companyName1: w2IncomeFormEntry?.employerCompany?.label || null,
      companyMappingId1: w2IncomeFormEntry?.employerCompany?.payrollProviderMappingId || null,
      incomeW2Type1: w2IncomeFormEntry?.w2EmploymentType || null,
      companyName2: secondW2IncomeFormEntry?.employerCompany?.label || null,
      companyMappingId2: secondW2IncomeFormEntry?.employerCompany?.payrollProviderMappingId || null,
      incomeW2Type2: secondW2IncomeFormEntry?.w2EmploymentType || null,
      companyName3: thirdW2IncomeFormEntry?.employerCompany?.label || null,
      companyMappingId3: thirdW2IncomeFormEntry?.employerCompany?.payrollProviderMappingId || null,
      incomeW2Type3: thirdW2IncomeFormEntry?.w2EmploymentType || null,
    },
  };
};

export const getTotalAnnualIncome = (incomeFormValues: IncomeFormValues) => {
  return incomeFormValues.incomes.reduce((totalAnnualIncome, incomeFormEntry) => {
    return totalAnnualIncome + (Number(removeNonDigits(incomeFormEntry.annualIncome)) || 0);
  }, 0);
};

export const getHasReachedMaximumIncomeSources = (selectedIncomeTypes: CombinedIncomeType[]) => {
  return selectedIncomeTypes.length >= MAXIMUM_INCOME_SOURCES;
};

export const getAnnualIncomeCaption = (combinedIncomeType: CombinedIncomeType) => {
  switch (combinedIncomeType) {
    case BorrowerIncomeType.SelfEmployed:
      return 'Annual, after all write-offs and deductions';
    case BorrowerIncomeType.RentalIncome:
    case BorrowerIncomeType.Other:
      return 'Annual';
    case BorrowerIncomeType.HomemakerUnemployed:
      return '';
    default:
      return 'Annual Before Taxes';
  }
};
